import React, { useState, useRef, useEffect, useMemo, useId } from 'react';
import { Plus } from 'lucide-react';
import './InventoryFieldCombobox.css';

/**
 * Collect unique non-empty string values from inventory for a field (case-insensitive dedupe).
 */
export function collectInventoryFieldSuggestions(items, getValue) {
  const seen = new Map();
  for (const item of items || []) {
    const raw = getValue(item);
    if (raw == null) continue;
    const trimmed = String(raw).trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (!seen.has(key)) seen.set(key, trimmed);
  }
  return Array.from(seen.values()).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
}

const InventoryFieldCombobox = ({
  label,
  value = '',
  onChange,
  suggestions = [],
  placeholder,
  disabled = false,
  className = ''
}) => {
  const inputId = useId();
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const trimmed = String(value ?? '').trim();
  const lower = trimmed.toLowerCase();

  const filtered = useMemo(() => {
    if (!lower) return suggestions;
    return suggestions.filter((s) => s.toLowerCase().includes(lower));
  }, [suggestions, lower]);

  const exactMatch = useMemo(
    () => suggestions.some((s) => s.toLowerCase() === lower),
    [suggestions, lower]
  );

  const showAddNew = trimmed.length > 0 && !exactMatch;

  const optionCount = filtered.length + (showAddNew ? 1 : 0);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  useEffect(() => {
    setHighlightIndex(-1);
  }, [value, open]);

  const selectValue = (next) => {
    onChange(next);
    setOpen(false);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      e.preventDefault();
      return;
    }
    if (!open) return;

    if (e.key === 'Escape') {
      setOpen(false);
      setHighlightIndex(-1);
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i < optionCount - 1 ? i + 1 : 0));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i > 0 ? i - 1 : optionCount - 1));
      return;
    }
    if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      if (showAddNew && highlightIndex === filtered.length) {
        selectValue(trimmed);
      } else if (filtered[highlightIndex]) {
        selectValue(filtered[highlightIndex]);
      }
      return;
    }
  };

  useEffect(() => {
    if (!open || highlightIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-option-index="${highlightIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex, open]);

  return (
    <div className={`im-combobox-wrap ${className}`.trim()} ref={wrapRef}>
      {label ? (
        <label htmlFor={inputId} className="im-combobox-label">
          {label}
        </label>
      ) : null}
      <div className="im-combobox-input-wrap">
        <input
          id={inputId}
          type="text"
          className="im-combobox-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={open ? `${inputId}-listbox` : undefined}
          role="combobox"
        />
        {open && (filtered.length > 0 || showAddNew) && (
          <ul
            id={`${inputId}-listbox`}
            ref={listRef}
            className="im-combobox-list"
            role="listbox"
          >
            {filtered.map((option, idx) => (
              <li
                key={option}
                role="option"
                data-option-index={idx}
                aria-selected={highlightIndex === idx}
                className={
                  'im-combobox-option' + (highlightIndex === idx ? ' im-combobox-option--active' : '')
                }
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlightIndex(idx)}
                onClick={() => selectValue(option)}
              >
                {option}
              </li>
            ))}
            {showAddNew && (
              <li
                role="option"
                data-option-index={filtered.length}
                aria-selected={highlightIndex === filtered.length}
                className={
                  'im-combobox-option im-combobox-option--add' +
                  (highlightIndex === filtered.length ? ' im-combobox-option--active' : '')
                }
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlightIndex(filtered.length)}
                onClick={() => selectValue(trimmed)}
              >
                <Plus size={14} aria-hidden />
                <span>Add &ldquo;{trimmed}&rdquo;</span>
              </li>
            )}
          </ul>
        )}
        {open && filtered.length === 0 && !showAddNew && suggestions.length === 0 && (
          <ul className="im-combobox-list im-combobox-list--empty" role="listbox">
            <li className="im-combobox-empty">No existing values — type to add a new one</li>
          </ul>
        )}
      </div>
    </div>
  );
};

export default InventoryFieldCombobox;
